import {
  InputIcon,
  InputSelectOptions,
  InputTypeSelect,
  SelectDropdown,
} from 'components/inputs/styles';
import Icon from 'components/icons';
import { Dropdown } from 'antd';
import { useState } from 'react';
import styled from 'styled-components/macro';
import { getModalPopupContainer } from 'utils/popupContainer';

const SelectSearchInput = styled.input`
  border: 1px solid var(--dusty-gray, #e9e9ed);
  border-radius: 6px;
  color: var(--black-dark, #1a2535);
  font-family: var(--font-family-base);
  font-size: 12px;
  height: 34px;
  margin: 5px;
  padding: 0 10px;
  width: calc(100% - 10px);

  &:focus {
    border-color: var(--lilac, #a793f6);
    outline: none;
  }
`;

interface InputSelectProps {
  value: string | number;
  icon?: string;
  onChange?: (selected: string | number) => void;
  options?: {
    label: string | number | React.ReactNode;
    value: string | number;
    icon?: string;
  }[];
  defaultOpen?: boolean;
  placeHolder?: string;
  error?: boolean;
  disabled?: boolean;
  maxDropdownHeight?: string;
  searchable?: boolean;
  searchLabel?: string;
}

const InputSelect: React.FC<InputSelectProps> = ({
  value,
  icon,
  onChange,
  options,
  defaultOpen,
  placeHolder,
  error,
  disabled,
  maxDropdownHeight,
  searchable = false,
  searchLabel,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [searchQuery, setSearchQuery] = useState('');
  const handleSelection = (value: string | number) => {
    onChange?.(value);
    setOpen(false);
    setSearchQuery('');
  };
  const filteredOptions = (options || []).filter(({ label }) =>
    String(label).toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const handleVisibleChange = (visible: boolean) => {
    setOpen(visible);

    if (!visible) {
      setSearchQuery('');
    }
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setSearchQuery('');
      return;
    }

    event.stopPropagation();
  };

  return (
    <Dropdown
      overlay={
        <SelectDropdown $maxHeight={maxDropdownHeight}>
          {searchable && (
            <SelectSearchInput
              aria-label={searchLabel}
              onChange={(event) => setSearchQuery(event.target.value)}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchLabel}
              value={searchQuery}
            />
          )}
          {filteredOptions.map(({ label, value, icon }) => {
            return (
              <InputSelectOptions key={value} onClick={() => handleSelection(value)}>
                {icon && <InputIcon src={icon}></InputIcon>}
                {label}
              </InputSelectOptions>
            );
          })}
        </SelectDropdown>
      }
      trigger={['click']}
      visible={open}
      disabled={disabled}
      onVisibleChange={handleVisibleChange}
      getPopupContainer={getModalPopupContainer}
    >
      <InputTypeSelect
        aria-disabled={disabled}
        error={error}
        disabled={disabled}
        isPlaceholder={!value}
      >
        {icon && <InputIcon src={icon}></InputIcon>}
        {value || placeHolder}
        <Icon icon={'ChevronDown'} size={'smaller'} />
      </InputTypeSelect>
    </Dropdown>
  );
};

export default InputSelect;
